import React from 'react';
import { Box, ButtonBase, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Visibility, Delete, Image, NoPhotography } from '@mui/icons-material';
import { ReactComponent as PlusIcon } from '../../static/images/svg/ic_round-plus.svg';
import ImageSourcePickerDialog from '../image-source-picker/image-source-picker-dialog.component';
import { useState } from 'react';
import AvatarDialog from './avatar-dialog';
import AlertDialog from '../dialog/alert-dialog.component';
import i18n from '../../i18n';
import { createStyles, makeStyles } from '@mui/styles';
import ButtonComponent from '../button/button.component';
import { getRBPStringId } from '../../page/main/main-page-states';
import type { ImageUploadSpec } from '../../utils/image-upload-spec';

/**
 * 头像 / 通用图片卡片：选图走统一 `ImageSourcePickerDialog`（本地 + 网络），确认后回传 File。
 * @param props
 * @returns
 */
export default function AvatarCard(props: {
    // 名称
    name?: string;
    // 头像
    avatarUrl?: string;
    // 大图
    avatarUrlMax?: string;
    // 更换头像
    onChangeAvatarUrl?: (avatarUrl: string, file: File) => void;
    // 删除头像
    onDelete?: () => void;
    // 是否禁用上传图片
    isDisabledUpload?: boolean;
    // 头像容器尺寸（px），不传时保持组件默认 140
    size?: number;
    /**
     * 紧凑槽位（集团 / 餐厅建档 64px 图位）：空态徽标与文案、上传后的悬浮操作按钮按原型同比缩小。
     * 仅显式传入时生效，不影响其他页面既有槽位样式。
     */
    compact?: boolean;
    /**
     * 画幅比例：`legacy` 为正方形（宽高均 `size`）；`firstScreenGallery` 为首页营销槽位（默认 256×160）。
     * 空态视觉统一：虚线框、灰底、主题色加号 + 「添加图片」文案，仅槽位宽高不同。
     */
    uploadEmptyVariant?: 'legacy' | 'firstScreenGallery';
    /** 与 `firstScreenGallery` 配套：槽位宽度（px），默认 256 */
    slotWidth?: number;
    /** 与 `firstScreenGallery` 配套：槽位高度（px），默认 160 */
    slotHeight?: number;
    /**
     * 图位下方 `name` 文案版式：`default` 为历史居中+省略；`firstScreenSlot` 对齐首页与营销「基础设置」等稿（body2、次要色、左齐、可换行）。
     */
    slotCaptionLayout?: 'default' | 'firstScreenSlot';
    /** 为 true 时仅在图片槽位上显示 loading，不遮挡下方名称/说明文案（如基础设置「餐厅环境图」） */
    busy?: boolean;
    /**
     * 空态上传区主文案（如 QOP 模板槽「上传模版」）；不传时沿用 `first_screen_text_add_image` 的 i18n，避免影响其他页面。
     */
    emptyUploadLabel?: string;
    /**
     * 首页营销 `firstScreenGallery` 空槽：点击走自定义逻辑（如打开选菜弹窗），不唤起选图；与虚线空态视觉一致。
     */
    onGalleryEmptyClick?: () => void;
    /** 当前图片入口的裁剪比例与建议输出规格。 */
    uploadSpec: ImageUploadSpec;
    /**
     * 网络搜图餐厅码。显式优先；否则 fallback `getRBPStringId()`；皆空则弹窗禁用网络 Tab。
     */
    stringId?: string;
    /** 是否禁用网络图片来源；表单未提供餐厅码时应显式关闭该能力。 */
    disableNetworkUpload?: boolean;
    /** 选择器弹窗标题；默认「编辑图片」 */
    pickerTitle?: string;
}) {
    // 图片加载失败
    const [error, setError] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [success, setSuccess] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [preViewOpen, setPreviewOpen] = useState(false);
    const theme = useTheme();
    const { avatarUrl, avatarUrlMax, name, isDisabledUpload, busy: busyProp } = props;
    const busy = Boolean(busyProp);
    /** 图位说明是否按首屏营销基础设置稿式展示 */
    const slotCaptionFirstScreen = props.slotCaptionLayout === 'firstScreenSlot';
    /** 头像槽位尺寸：紧凑模式默认 64px，其他模式保持历史 140px。 */
    const avatarSize = props.size ?? (props.compact ? 64 : 140);
    /** 是否使用首页与营销大图位（非正方形）画幅 */
    const isGallery = props.uploadEmptyVariant === 'firstScreenGallery';
    /** 槽位宽度（px） */
    const frameW = isGallery ? (props.slotWidth ?? 256) : avatarSize;
    /** 槽位高度（px） */
    const frameH = isGallery ? (props.slotHeight ?? 160) : avatarSize;
    /** 圆角 8px：与 Figma 1838:52035 槽位一致（基于 theme.shape 加倍或回退 8） */
    const slotRadiusPx =
        typeof theme.shape.borderRadius === 'number' ? Math.max(8, theme.shape.borderRadius * 2) : 8;
    const classes = useStyles();
    /** 是否为建档紧凑槽位（64px），决定空态与悬浮操作按钮的尺寸档位 */
    const isCompact = Boolean(props.compact);
    /** 空态主色加号徽标边长（px）：紧凑槽位按原型 22 */
    const plusBadgeSize = isCompact ? 22 : 32;
    /** 空态加号图标边长（px） */
    const plusIconSize = isCompact ? 14 : 20;
    /** 空态徽标圆角（px）：紧凑槽位按原型 6 */
    const plusBadgeRadiusPx = isCompact ? 6 : slotRadiusPx;
    /** 徽标与下方文案的间距（theme.spacing 单位，基数 4px）：紧凑槽位约 3px */
    const emptyContentGap = isCompact ? 0.75 : 2;
    /** 空态文案字号：紧凑槽位用 12px caption，避免在 64px 框内换行溢出 */
    const emptyCaptionVariant = isCompact ? 'caption' : 'subtitle1';
    /** 空态上传文案：紧凑槽位按原型只显示「图片」 */
    const emptyCaptionText =
        props.emptyUploadLabel ??
        i18n.t(isCompact ? 'general_text_image' : 'first_screen_text_add_image');

    /**
     * 网络搜图餐厅码：显式 > 当前店 fallback。
     */
    const resolvedStringId = (() => {
        if (props.disableNetworkUpload) {
            return '';
        }
        const explicit = String(props.stringId || '').trim();
        if (explicit) {
            return explicit;
        }
        try {
            return String(getRBPStringId() || '').trim();
        } catch {
            return '';
        }
    })();

    /**
     * 打开统一图片选择器。
     */
    const openPicker = () => {
        if (isDisabledUpload || busy) {
            return;
        }
        setPickerOpen(true);
    };

    /**
     * 选择器确认：回传预览与 File。
     */
    const handlePickerConfirm = (payload: { previewUrl: string; file: File }) => {
        props.onChangeAvatarUrl?.(payload.previewUrl, payload.file);
        setPickerOpen(false);
    };

    // 图片地址解析失败时处理
    const onError = () => {
        setError(true);
    };

    React.useEffect(() => {
        setError(false);
    }, [avatarUrl]);

    return (
        <>
            <Box
                justifyContent="center"
                alignItems={slotCaptionFirstScreen && name ? 'flex-start' : 'center'}
                display="flex"
                flexDirection="column"
            >
                {avatarUrl && !error ? (
                    <Box
                        className={classes.imgBox}
                        style={{ width: frameW, height: frameH, borderRadius: `${slotRadiusPx}px` }}
                    >
                        <div className={classes.avatarUpload}>
                            <img
                                alt=""
                                className={classes.avatar}
                                src={avatarUrl}
                                onLoad={() => {
                                    setSuccess(true);
                                }}
                                onError={onError}
                            />
                        </div>
                        <Box display={success ? 'block' : 'none'}>
                            <Box
                                className={`${classes.iconBox} ${isCompact ? classes.iconBoxCompact : ''}`}
                            >
                                {isCompact ? (
                                    <>
                                        <CompactSlotIconButton
                                            title={i18n.t('general_text_preview_picture')}
                                            onClick={() => setPreviewOpen(true)}
                                        >
                                            <Visibility />
                                        </CompactSlotIconButton>
                                        {props.onChangeAvatarUrl && !isDisabledUpload && (
                                            <CompactSlotIconButton
                                                title={i18n.t('general_text_change_image')}
                                                onClick={openPicker}
                                            >
                                                <Image />
                                            </CompactSlotIconButton>
                                        )}
                                        {props.onDelete && (
                                            <CompactSlotIconButton
                                                title={i18n.t('general_text_delete')}
                                                color="secondary"
                                                onClick={() => setDeleteConfirmOpen(true)}
                                            >
                                                <Delete />
                                            </CompactSlotIconButton>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <ButtonComponent
                                            titleText={i18n.t('general_text_preview_picture')}
                                            handleClick={() => {
                                                setPreviewOpen(true);
                                            }}
                                            children={<Visibility />}
                                        />
                                        {props.onChangeAvatarUrl && !isDisabledUpload && (
                                            <UploadImageIcon onClick={openPicker} />
                                        )}
                                        {props.onDelete && (
                                            <ButtonComponent
                                                color="secondary"
                                                titleText={i18n.t('general_text_delete')}
                                                handleClick={() => {
                                                    setDeleteConfirmOpen(true);
                                                }}
                                                children={<Delete />}
                                            />
                                        )}
                                    </>
                                )}
                            </Box>
                        </Box>
                        {busy ? (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    zIndex: 25,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'var(--overlay-800-a25)',
                                    borderRadius: `${slotRadiusPx}px`,
                                    pointerEvents: 'auto',
                                }}
                                aria-busy="true"
                                aria-live="polite"
                            >
                                <CircularProgress size={32} color="primary" />
                            </Box>
                        ) : null}
                    </Box>
                ) : isGallery && props.onGalleryEmptyClick ? (
                    <ButtonBase
                        focusRipple
                        type="button"
                        disabled={busy || Boolean(isDisabledUpload)}
                        onClick={() => props.onGalleryEmptyClick?.()}
                        aria-label={emptyCaptionText}
                        sx={{
                            display: 'block',
                            p: 0,
                            borderRadius: `${slotRadiusPx}px`,
                            width: frameW,
                            height: frameH,
                        }}
                    >
                        <Box
                            className={classes.avatarBox}
                            sx={{
                                position: 'relative',
                                width: frameW,
                                height: frameH,
                                p: 0,
                                borderRadius: `${slotRadiusPx}px`,
                                border: '1px dashed var(--stroke-neutral-800-a25)',
                                bgcolor: 'var(--fill-neutral-200)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: emptyContentGap,
                                    width: '100%',
                                    height: '100%',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: plusBadgeSize,
                                        height: plusBadgeSize,
                                        borderRadius: `${plusBadgeRadiusPx}px`,
                                        bgcolor: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'var(--fill-neutral-100)',
                                        flex: 'none',
                                    }}
                                    aria-hidden
                                >
                                    <PlusIcon width={plusIconSize} height={plusIconSize} />
                                </Box>
                                <Typography
                                    variant={emptyCaptionVariant}
                                    sx={{
                                        color: 'text.secondary',
                                        textAlign: 'center',
                                        ...(isCompact ? {} : { fontWeight: 700 }),
                                    }}
                                >
                                    {emptyCaptionText}
                                </Typography>
                            </Box>
                            {busy ? (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        zIndex: 25,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'var(--overlay-800-a25)',
                                        borderRadius: `${slotRadiusPx}px`,
                                        pointerEvents: 'auto',
                                    }}
                                    aria-busy="true"
                                    aria-live="polite"
                                >
                                    <CircularProgress size={32} color="primary" />
                                </Box>
                            ) : null}
                        </Box>
                    </ButtonBase>
                ) : !isDisabledUpload ? (
                    <ButtonBase
                        focusRipple
                        type="button"
                        disabled={busy}
                        onClick={openPicker}
                        aria-label={emptyCaptionText}
                        sx={{
                            display: 'block',
                            p: 0,
                            borderRadius: `${slotRadiusPx}px`,
                            width: frameW,
                            height: frameH,
                        }}
                    >
                        <Box
                            className={classes.avatarBox}
                            sx={{
                                position: 'relative',
                                width: frameW,
                                height: frameH,
                                p: 0,
                                borderRadius: `${slotRadiusPx}px`,
                                border: '1px dashed var(--stroke-neutral-800-a25)',
                                bgcolor: 'var(--fill-neutral-200)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: emptyContentGap,
                                    width: '100%',
                                    height: '100%',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: plusBadgeSize,
                                        height: plusBadgeSize,
                                        borderRadius: `${plusBadgeRadiusPx}px`,
                                        bgcolor: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'var(--fill-neutral-100)',
                                        flex: 'none',
                                    }}
                                    aria-hidden
                                >
                                    <PlusIcon width={plusIconSize} height={plusIconSize} />
                                </Box>
                                <Typography
                                    variant={emptyCaptionVariant}
                                    sx={{
                                        color: 'text.secondary',
                                        textAlign: 'center',
                                        ...(isCompact ? {} : { fontWeight: 700 }),
                                    }}
                                >
                                    {emptyCaptionText}
                                </Typography>
                            </Box>
                            {busy ? (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        zIndex: 25,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'var(--overlay-800-a25)',
                                        borderRadius: `${slotRadiusPx}px`,
                                        pointerEvents: 'auto',
                                    }}
                                    aria-busy="true"
                                    aria-live="polite"
                                >
                                    <CircularProgress size={32} color="primary" />
                                </Box>
                            ) : null}
                        </Box>
                    </ButtonBase>
                ) : (
                    <div>
                        <Box
                            className={classes.avatarBox}
                            style={{
                                width: frameW,
                                height: frameH,
                                borderRadius: `${slotRadiusPx}px`,
                                opacity: 0.6,
                            }}
                            sx={{
                                position: 'relative',
                                p: 0,
                                border: '1px dashed var(--stroke-neutral-800-a25)',
                                bgcolor: 'var(--fill-neutral-200)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Box
                                className={classes.avatarUpload}
                                style={{ cursor: 'default' }}
                                sx={{
                                    flexDirection: 'column',
                                    gap: emptyContentGap,
                                    color: 'text.secondary',
                                }}
                            >
                                <NoPhotography sx={{ fontSize: isCompact ? 20 : 32 }} />
                                <Typography
                                    variant={emptyCaptionVariant}
                                    sx={{
                                        textAlign: 'center',
                                        ...(isCompact ? {} : { fontWeight: 700 }),
                                    }}
                                >
                                    {i18n.t('general_text_no_photo')}
                                </Typography>
                            </Box>
                            {busy ? (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        zIndex: 25,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'var(--overlay-800-a25)',
                                        borderRadius: `${slotRadiusPx}px`,
                                        pointerEvents: 'auto',
                                    }}
                                    aria-busy="true"
                                    aria-live="polite"
                                >
                                    <CircularProgress size={32} color="primary" />
                                </Box>
                            ) : null}
                        </Box>
                    </div>
                )}
                {name ? (
                    slotCaptionFirstScreen ? (
                        <Typography
                            component="div"
                            variant="body2"
                            sx={{
                                width: frameW,
                                mt: 2,
                                color: 'var(--text-secondary)',
                                textAlign: 'left',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                            }}
                        >
                            {name}
                        </Typography>
                    ) : (
                        <Typography className={classes.avatarName} style={{ width: frameW }}>
                            {name}
                        </Typography>
                    )
                ) : null}
            </Box>
            {pickerOpen ? (
                <ImageSourcePickerDialog
                    open={true}
                    onClose={() => setPickerOpen(false)}
                    onConfirm={handlePickerConfirm}
                    uploadSpec={props.uploadSpec}
                    stringId={resolvedStringId || undefined}
                    title={props.pickerTitle}
                />
            ) : null}
            {preViewOpen && (
                <AvatarDialog
                    open={true}
                    onClose={() => {
                        setPreviewOpen(false);
                    }}
                    data={[
                        {
                            imgSrc: avatarUrlMax || avatarUrl || '',
                            imgName: '',
                        },
                    ]}
                />
            )}
            {props.onDelete && (
                <AlertDialog
                    open={deleteConfirmOpen}
                    variant="delete"
                    title={i18n.t('general_text_delete')}
                    highlightText={name?.trim() ? name : undefined}
                    onCancel={() => setDeleteConfirmOpen(false)}
                    onClose={() => setDeleteConfirmOpen(false)}
                    onConfirm={() => {
                        setDeleteConfirmOpen(false);
                        props.onDelete?.();
                    }}
                />
            )}
        </>
    );
}

const useStyles = makeStyles(() =>
    createStyles({
        fileInput: {
            display: 'none',
        },
        avatarUpload: {
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
            color: '#637381',
            borderRadius: '4px',
        },
        avatar: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            cursor: 'pointer',
        },
        avatarBox: {
            width: '140px',
            height: '140px',
            borderRadius: '4px',
        },
        imgBox: {
            width: '140px',
            height: '140px',
            borderRadius: '4px',
            overflow: 'hidden',
            position: 'relative',
        },
        // 紧凑槽位（64px）悬浮操作区：按钮居中并排，间距 4px，去掉 36px 钮的外间距
        iconBoxCompact: {
            justifyContent: 'center',
            gap: '4px',
        },
        iconBox: {
            justifyContent: 'space-around',
            alignItems: 'center',
            position: 'absolute',
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
            display: 'flex',
            opacity: 0,
            background: '#4191ff26',
            '&>span': {
                marginLeft: '0 !important',
                padding: '0 !important',
            },
            '&:hover': {
                opacity: 1,
            },
        },
        icon: {
            width: '20px',
            height: '20px',
            cursor: 'pointer',
            marginLeft: '6px',
            color: '#4191ff',
            backgroundColor: '#4191ff26',
        },
        btn: {
            color: '#4191ff',
            backgroundColor: '#4191ff26',
            borderRadius: '4px',
            display: 'flex',
            cursor: 'pointer',
            width: '40px',
            height: '40px',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
                color: '#4191ff',
                backgroundColor: '#b8e0fe',
            },
        },
        btnLabel: {
            display: 'flex',
            alignItems: 'center',
            padding: '8px',
            marginLeft: '-8px',
        },
        avatarName: {
            padding: '5px 0',
            width: '140px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'center',
        },
    }),
);

/**
 * 紧凑槽位（64px 建档图位）悬浮操作按钮：26×26 圆钮 + 16px 图标。
 * 公共 `ButtonComponent` 以 `!important` 锁死 36×36，无法塞进 64px 图位，故此处单独用 `IconButton`。
 * @param props.title Tooltip 与无障碍文案
 * @param props.color 配色，删除操作传 `secondary`，其余用主色
 * @param props.onClick 点击回调
 * @param props.children 图标元素
 */
function CompactSlotIconButton(props: {
    /** Tooltip 与 aria-label 文案 */
    title: string;
    /** 按钮配色，默认主色 */
    color?: 'primary' | 'secondary';
    /** 点击回调 */
    onClick: () => void;
    /** 按钮内图标 */
    children: React.ReactNode;
}) {
    /** 当前配色对应的主题色键 */
    const paletteKey = props.color === 'secondary' ? 'secondary' : 'primary';
    return (
        <Tooltip title={props.title} placement="top">
            <IconButton
                aria-label={props.title}
                onClick={(e) => {
                    e.stopPropagation();
                    props.onClick();
                }}
                sx={{
                    width: 26,
                    height: 26,
                    p: 0,
                    bgcolor: `${paletteKey}.main`,
                    color: 'var(--fill-neutral-100)',
                    '&:hover': {
                        bgcolor: `${paletteKey}.dark`,
                    },
                    // 图标尺寸而非文案字号，与 26px 圆钮匹配
                    '& .MuiSvgIcon-root': { fontSize: 16 },
                }}
            >
                {props.children}
            </IconButton>
        </Tooltip>
    );
}

/**
 * 上传/更换图片入口图标：点击打开统一选择器（由父组件传入 onClick）。
 */
export function UploadImageIcon(props: {
    /** 点击回调（打开 ImageSourcePickerDialog） */
    onClick: () => void;
}) {
    return (
        <ButtonComponent
            titleText={i18n.t('general_text_change_image')}
            handleClick={() => {
                props.onClick();
            }}
        >
            <Image />
        </ButtonComponent>
    );
}

/**
 * 独立「更换图片」入口：打开统一选择器并回传裁切后 File。
 */
export function UploadImage(props: {
    // 上传图片
    onChange: (file: File) => void;
    /** 当前图片入口的裁剪比例与建议输出规格。 */
    uploadSpec: ImageUploadSpec;
    /** 网络搜图餐厅码 */
    stringId?: string;
    /** 弹窗标题 */
    pickerTitle?: string;
}) {
    const [pickerOpen, setPickerOpen] = useState(false);

    /**
     * 解析餐厅码。
     */
    const resolvedStringId = (() => {
        const explicit = String(props.stringId || '').trim();
        if (explicit) {
            return explicit;
        }
        try {
            return String(getRBPStringId() || '').trim();
        } catch {
            return '';
        }
    })();

    return (
        <>
            <UploadImageIcon
                onClick={() => setPickerOpen(true)}
            />
            {pickerOpen ? (
                <ImageSourcePickerDialog
                    open={true}
                    onClose={() => setPickerOpen(false)}
                    onConfirm={(payload) => {
                        props.onChange(payload.file);
                        setPickerOpen(false);
                    }}
                    uploadSpec={props.uploadSpec}
                    stringId={resolvedStringId || undefined}
                    title={props.pickerTitle}
                />
            ) : null}
        </>
    );
}
